import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { teamsAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Plus,
  Users,
  MoreVertical,
  Trash2,
  UserPlus,
  UserMinus,
  LogOut,
  Mail,
  Crown,
  Shield
} from 'lucide-react';

const TeamsPage = () => {
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showGroupDetail, setShowGroupDetail] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  
  const [groupName, setGroupName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteToken, setInviteToken] = useState('');

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      const response = await teamsAPI.list();
      setGroups(response.data);
    } catch (error) {
      console.error('Error fetching teams:', error);
      toast.error('Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  const openGroupDetail = async (groupId) => {
    try {
      const response = await teamsAPI.get(groupId);
      setSelectedGroup(response.data);
      setShowGroupDetail(true);
    } catch (error) {
      toast.error('Failed to load team details');
    }
  };

  const handleCreateGroup = async () => {
    if (!groupName.trim()) {
      toast.error('Team name is required');
      return;
    }

    try {
      await teamsAPI.create({ name: groupName });
      toast.success('Team created');
      setShowCreateDialog(false);
      setGroupName('');
      fetchGroups();
    } catch (error) {
      toast.error('Failed to create team');
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Delete this team? All associated tasks will lose their team.')) return;
    
    try {
      await teamsAPI.delete(groupId);
      toast.success('Team deleted');
      fetchGroups();
      if (selectedGroup?.group_id === groupId) {
        setShowGroupDetail(false);
        setSelectedGroup(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete team');
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) {
      toast.error('Email is required');
      return;
    }

    try {
      const response = await teamsAPI.invite(selectedGroup.group_id, inviteEmail);
      setInviteToken(response.data.token);
      toast.success('Invite sent');
      setInviteEmail('');
      // Refresh group details
      const updated = await teamsAPI.get(selectedGroup.group_id);
      setSelectedGroup(updated.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invite');
    }
  };

  const handleCancelInvite = async (email) => {
    try {
      await teamsAPI.cancelInvite(selectedGroup.group_id, email);
      toast.success('Invite cancelled');
      const updated = await teamsAPI.get(selectedGroup.group_id);
      setSelectedGroup(updated.data);
    } catch (error) {
      toast.error('Failed to cancel invite');
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm('Remove this member from the team?')) return;
    
    try {
      await teamsAPI.removeMember(selectedGroup.group_id, memberId);
      toast.success('Member removed');
      const updated = await teamsAPI.get(selectedGroup.group_id);
      setSelectedGroup(updated.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    }
  };

  const handleLeaveGroup = async (groupId) => {
    if (!window.confirm('Leave this team?')) return;
    
    try {
      await teamsAPI.leave(groupId);
      toast.success('Left team');
      fetchGroups();
      if (selectedGroup?.group_id === groupId) {
        setShowGroupDetail(false);
        setSelectedGroup(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to leave team');
    }
  };

  const handleAcceptInvite = async () => {
    const token = window.prompt('Enter invite token:');
    if (!token) return;
    
    try {
      await teamsAPI.acceptInvite(token);
      toast.success('Joined team');
      fetchGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid or expired invite');
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';
  };

  const getMemberRole = (members, userId) => {
    const member = members?.find(m => m.user_id === userId);
    return member?.role || 'member';
  };

  const isOwnerOrAdmin = (group) => {
    const role = getMemberRole(group.members, user?.user_id);
    return role === 'owner' || role === 'admin';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="teams-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-heading">Teams</h1>
          <p className="text-slate-600">Manage your teams and collaborators</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleAcceptInvite}>
            <Mail className="mr-2 h-4 w-4" />
            Accept Invite
          </Button>
          <Button onClick={() => setShowCreateDialog(true)} data-testid="create-team-btn">
            <Plus className="mr-2 h-4 w-4" />
            New Team
          </Button>
        </div>
      </div>

      {/* Groups Grid */}
      {groups.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="h-8 w-8 text-slate-400" />
            </div>
            <h3 className="font-medium text-slate-900">No teams yet</h3>
            <p className="text-slate-600 mt-1">Create a team to start collaborating</p>
            <Button onClick={() => setShowCreateDialog(true)} className="mt-4">
              <Plus className="mr-2 h-4 w-4" />
              Create Team
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {groups.map((group) => {
            const role = getMemberRole(group.members, user?.user_id);
            return (
              <Card key={group.group_id} className="card-hover cursor-pointer" onClick={() => openGroupDetail(group.group_id)}>
                <CardHeader className="flex flex-row items-start justify-between space-y-0">
                  <div>
                    <CardTitle className="font-heading text-lg">{group.name}</CardTitle>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="secondary" className="text-xs">
                        {group.members?.length || 0} members
                      </Badge>
                      {role === 'owner' && (
                        <Badge className="bg-amber-100 text-amber-800 text-xs">
                          <Crown className="h-3 w-3 mr-1" />
                          Owner
                        </Badge>
                      )}
                      {role === 'admin' && (
                        <Badge className="bg-indigo-100 text-indigo-800 text-xs">
                          <Shield className="h-3 w-3 mr-1" />
                          Admin
                        </Badge>
                      )}
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="sm">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {isOwnerOrAdmin(group) && (
                        <DropdownMenuItem onClick={(e) => {
                          e.stopPropagation();
                          setSelectedGroup(group);
                          setShowInviteDialog(true);
                        }}>
                          <UserPlus className="mr-2 h-4 w-4" />
                          Invite Members
                        </DropdownMenuItem>
                      )}
                      {role !== 'owner' && (
                        <DropdownMenuItem onClick={(e) => {
                          e.stopPropagation();
                          handleLeaveGroup(group.group_id);
                        }}>
                          <LogOut className="mr-2 h-4 w-4" />
                          Leave Team
                        </DropdownMenuItem>
                      )}
                      {role === 'owner' && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteGroup(group.group_id);
                            }}
                            className="text-red-600"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete Team
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </CardHeader>
                <CardContent>
                  <div className="flex -space-x-2">
                    {group.members?.slice(0, 5).map((member, idx) => (
                      <Avatar key={member.user_id} className="h-8 w-8 border-2 border-white">
                        <AvatarImage src={member.picture} />
                        <AvatarFallback className="bg-indigo-100 text-indigo-600 text-xs">
                          {getInitials(member.name)}
                        </AvatarFallback>
                      </Avatar>
                    ))}
                    {group.members?.length > 5 && (
                      <div className="h-8 w-8 rounded-full bg-slate-100 border-2 border-white flex items-center justify-center text-xs text-slate-600">
                        +{group.members.length - 5}
                      </div>
                    )}
                  </div>
                  {group.pending_invites?.length > 0 && (
                    <p className="text-sm text-slate-500 mt-3">
                      {group.pending_invites.length} pending invite(s)
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Group Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Create Team</DialogTitle>
            <DialogDescription>Create a new team to collaborate.</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Team Name</Label>
            <Input
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder="Enter team name"
              className="mt-2"
              data-testid="team-name-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateGroup} data-testid="save-team-btn">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Invite to {selectedGroup?.name}</DialogTitle>
            <DialogDescription>Send an invitation to join this team.</DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label>Email Address</Label>
              <Input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="email@example.com"
                className="mt-2"
              />
            </div>
            {inviteToken && (
              <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                <p className="text-sm text-emerald-800 font-medium">Invite sent!</p>
                <p className="text-xs text-emerald-600 mt-1">Token: {inviteToken}</p>
                <p className="text-xs text-emerald-600">Share this token with the invitee.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowInviteDialog(false);
              setInviteToken('');
            }}>
              Close
            </Button>
            <Button onClick={handleInvite}>Send Invite</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Group Detail Dialog */}
      <Dialog open={showGroupDetail} onOpenChange={(open) => {
        setShowGroupDetail(open);
        if (!open) setSelectedGroup(null);
      }}>
        <DialogContent className="max-w-2xl">
          {selectedGroup && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">{selectedGroup.name}</DialogTitle>
                <DialogDescription>
                  {selectedGroup.members?.length || 0} members
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-6 py-4">
                {/* Members */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium text-slate-900">Members</h3>
                    {isOwnerOrAdmin(selectedGroup) && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => setShowInviteDialog(true)}
                      >
                        <UserPlus className="mr-2 h-4 w-4" />
                        Invite
                      </Button>
                    )}
                  </div>
                  <div className="space-y-2">
                    {selectedGroup.members?.map((member) => (
                      <div 
                        key={member.user_id}
                        className="flex items-center justify-between p-3 rounded-lg border"
                      >
                        <div className="flex items-center gap-3">
                          <Avatar className="h-10 w-10">
                            <AvatarImage src={member.picture} />
                            <AvatarFallback className="bg-indigo-100 text-indigo-600">
                              {getInitials(member.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{member.name}</p>
                            <p className="text-sm text-slate-500">{member.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {member.role === 'owner' && (
                            <Badge className="bg-amber-100 text-amber-800">
                              <Crown className="h-3 w-3 mr-1" />
                              Owner
                            </Badge>
                          )}
                          {member.role === 'admin' && (
                            <Badge className="bg-indigo-100 text-indigo-800">
                              <Shield className="h-3 w-3 mr-1" />
                              Admin
                            </Badge>
                          )}
                          {isOwnerOrAdmin(selectedGroup) && member.role !== 'owner' && member.user_id !== user?.user_id && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleRemoveMember(member.user_id)}
                            >
                              <UserMinus className="h-4 w-4 text-red-500" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Pending Invites */}
                {selectedGroup.pending_invites?.length > 0 && (
                  <div>
                    <h3 className="font-medium text-slate-900 mb-3">Pending Invites</h3>
                    <div className="space-y-2">
                      {selectedGroup.pending_invites.map((invite) => (
                        <div 
                          key={invite.email}
                          className="flex items-center justify-between p-3 rounded-lg border border-dashed"
                        >
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center">
                              <Mail className="h-5 w-5 text-slate-400" />
                            </div>
                            <div>
                              <p className="font-medium">{invite.email}</p>
                              <p className="text-sm text-slate-500">Invite pending</p>
                            </div>
                          </div>
                          {isOwnerOrAdmin(selectedGroup) && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleCancelInvite(invite.email)}
                            >
                              Cancel
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setShowGroupDetail(false)}>
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamsPage;
